import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import {
  Box, Typography, Chip, IconButton, Divider, Collapse,
  Tooltip, Modal, Fade,
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { BudgetTable } from './BudgetRow';
import { EXP_STATUS_MAP } from '../../utils/constants/financeStatus.constant';
import formatThaiDate from '../../utils/functions/formatThaiDate';

const fmt = (n) =>
  (typeof n === 'number' ? n : 0).toLocaleString('th-TH', { minimumFractionDigits: 2 });

function InlineStat({ label, value, color = 'text.secondary', tooltip }) {
  const content = (
    <Box sx={{ px: 1.2, textAlign: 'center', flexShrink: 0 }}>
      <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1.2 }} noWrap>{label}</Typography>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color, lineHeight: 1.3 }} noWrap>฿{fmt(value)}</Typography>
    </Box>
  );
  return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
}

// ─── Level 2 inside modal: Budget accordion ──────────────────────────────────
function BudgetAccordion({ budget }) {
  const [open, setOpen] = useState(false);
  const exps = budget?.expenses || [];
  const budgetCode = budget ? `${budget.prefix || ''}${budget.budget_number}` : 'ไม่ระบุงบ';

  return (
    <Box sx={{ mb: 0.8, borderRadius: 1, border: '1px solid #fde68a', overflow: 'hidden' }}>
      {/* Budget header */}
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex',
alignItems: 'center',
px: 2,
py: 1,
cursor: 'pointer',
          backgroundColor: open ? '#fffbeb' : '#fefce8',
          borderLeft: open ? '3px solid #f59e0b' : '3px solid transparent',
          '&:hover': { backgroundColor: '#fffbeb' },
          transition: 'background-color 0.15s',
        }}
      >
        <IconButton size="small" disableRipple sx={{ mr: 1, color: 'text.secondary' }}>
          {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Chip label="งบ" size="small" sx={{ height: 15, fontSize: '0.5rem', fontWeight: 700, backgroundColor: '#fde68a', color: '#92400e', '& .MuiChip-label': { px: 0.5 } }} />
            <Typography fontWeight={700} fontSize="0.82rem" color="#92400e">{budgetCode}</Typography>
            <Typography fontSize="0.82rem" color="text.secondary">{budget?.name || '-'}</Typography>
          </Box>
          <Typography variant="caption" color="text.disabled">{exps.length} รายการ · งบ ฿{fmt(budget?.cost ?? 0)}</Typography>
        </Box>
        <Typography variant="body2" fontWeight={700} color="text.secondary">
          ฿{fmt(_.sumBy(exps, 'price'))}
        </Typography>
      </Box>

      {/* Level 3: Expense rows */}
      <Collapse in={open}>
        <Box sx={{ backgroundColor: '#fffff5' }}>
          {/* Col headers */}
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.6, backgroundColor: '#fef3c7', borderBottom: '1px solid #fde68a' }}>
            {[
              { label: 'รหัส', w: 130 },
              { label: 'รายละเอียด', w: 220 },
              { label: 'วันที่', w: 110 },
              { label: 'สถานะ', w: 110, align: 'center' },
              { label: 'ยอด (฿)', w: 120, align: 'right' },
            ].map((col) => (
              <Typography key={col.label} variant="caption" fontWeight={700} color="#92400e" noWrap
                sx={{ width: col.w, flexShrink: 0, textAlign: col.align || 'left' }}>
                {col.label}
              </Typography>
            ))}
          </Box>

          {exps.length === 0 ? (
            <Box sx={{ px: 3, py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">ไม่มีรายการ</Typography>
            </Box>
          ) : (
            _.orderBy(exps, ['dateRequest', 'date', 'createdAt'], ['desc']).map((exp, i) => {
              const cfg = EXP_STATUS_MAP[exp.status];
              const dateStr = exp.dateRequest || exp.date || exp.createdAt;
              return (
                <Box key={exp._id || i} sx={{
                  display: 'flex',
alignItems: 'center',
px: 2,
py: 0.8,
                  borderBottom: '1px solid #fef9c3',
                  '&:hover': { backgroundColor: '#fefce8' },
                }}>
                  <Typography variant="body2" fontWeight={700} color="#1d4ed8" noWrap sx={{ width: 130, flexShrink: 0 }}>
                    {exp.code || exp.expenses_code || '-'}
                  </Typography>
                  <Tooltip title={exp.name || exp.detail || '-'} placement="top">
                    <Typography variant="body2" noWrap sx={{ width: 220, flexShrink: 0, color: 'text.secondary' }}>
                      {exp.name || exp.detail || '-'}
                    </Typography>
                  </Tooltip>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ width: 110, flexShrink: 0, fontSize: '0.78rem' }}>
                    {dateStr ? formatThaiDate(dateStr) : '-'}
                  </Typography>
                  <Box sx={{ width: 110, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {cfg ? (
                      <Chip label={cfg.label} size="small"
                        sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, backgroundColor: cfg.bg, color: cfg.color, '& .MuiChip-label': { px: 0.6 } }} />
                    ) : (
                      <Typography variant="caption" color="text.disabled">{exp.status || '-'}</Typography>
                    )}
                  </Box>
                  <Typography variant="body2" fontWeight={700} noWrap sx={{ width: 120, flexShrink: 0, textAlign: 'right', color: '#1e293b' }}>
                    {fmt(exp.price || 0)}
                  </Typography>
                </Box>
              );
            })
          )}

          {/* Budget subtotal */}
          {exps.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 0.8, borderTop: '1px solid #fde68a', backgroundColor: '#fef9c3' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>รวม</Typography>
              <Typography variant="body2" fontWeight={700}>฿{fmt(_.sumBy(exps, 'price'))}</Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Level 3: Project row ────────────────────────────────────────────────────
export default function ProjectRow({ expenses, payment }) {
  const [open, setOpen] = useState(false);
  const [projModal, setProjModal] = useState(false);
  const budgetRows = useSelector((state) => state.budget?.rows || []);
  const projectRows = useSelector((state) => state.project?.rows || []);

  const firstExp = expenses[0];
  const projectName = firstExp?.project?.name || 'ไม่ระบุโครงการ';
  const projectNumber = firstExp?.project?.project_number || '';
  const projectId = String(firstExp?.project?._id || firstExp?.project?.id || '');

  const projectData = projectRows.find((p) => String(p._id) === projectId || String(p.id) === projectId);
  const contractValue = projectData?.cost ?? 0;
  const laborCost = projectData?.labur_cost ?? 0;
  const vat = projectData?.vat ?? 0;
  const customer = projectData?.customer || '';

  const budgetIds = [...new Set(expenses.map((e) => e.budget?._id).filter(Boolean).map(String))];
  const projectBudgets = budgetIds.map((id) => budgetRows.find((b) => String(b._id) === id)).filter(Boolean);

  const totalBudget = _.sumBy(projectBudgets, 'cost') || 0;

  const getStatusAmt = (b, statusKey) => {
    if (Array.isArray(b?.totalStatus)) {
      return b.totalStatus.find((s) => s.status === statusKey)?.totalExpenses ?? 0;
    }
    return b?.totalExpensesByStatus?.[statusKey] ?? 0;
  };
  const totalSuccess = _.sumBy(projectBudgets, (b) => getStatusAmt(b, 'SUCCESS'));
  const totalApprove = _.sumBy(projectBudgets, (b) => getStatusAmt(b, 'APPROVE'));
  const remaining = totalBudget - totalSuccess - totalApprove;
  const isOver = remaining < 0;
  const hasBudget = totalBudget > 0;

  const budgetGroups = _.groupBy(
    expenses,
    (e) => e.budget?._id || e.budget?.budget_number || '__none__',
  );
  const budgetKeys = Object.keys(budgetGroups);

  return (
    <Box sx={{ mb: 1, borderRadius: 1.5, border: '1px solid #ede9fe', overflow: 'hidden', backgroundColor: '#fff' }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex',
alignItems: 'center',
px: 2,
py: 1,
cursor: 'pointer',
          borderLeft: open ? '3px solid #8b5cf6' : '3px solid transparent',
          backgroundColor: open ? '#faf5ff' : 'transparent',
          transition: 'background-color 0.15s, border-left-color 0.15s',
          '&:hover': { backgroundColor: open ? '#faf5ff' : '#fdf4ff' },
        }}
      >
        <IconButton size="small" sx={{ mr: 1, color: open ? '#8b5cf6' : 'text.secondary', flexShrink: 0 }} disableRipple>
          {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>

        <Box sx={{ flexShrink: 0, mr: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Chip label="โครงการ" size="small" sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, backgroundColor: '#ede9fe', color: '#7c3aed', borderRadius: 0.5, '& .MuiChip-label': { px: 0.6 } }} />
            <Typography fontWeight={600} fontSize="0.85rem" noWrap>{projectName}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {projectNumber && (
              <Tooltip title="คลิกดูรายละเอียดโครงการ">
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="#7c3aed"
                  onClick={(e) => { e.stopPropagation(); setProjModal(true); }}
                  sx={{ cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                >
                  {projectNumber}
                </Typography>
              </Tooltip>
            )}
            {projectNumber && <Typography variant="caption" color="text.disabled">•</Typography>}
            <Typography variant="caption" color="text.secondary">
              {expenses.length} รายการ • {budgetKeys.length} งบประมาณ
            </Typography>
          </Box>
        </Box>

        {hasBudget && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, '& > *:not(:last-child)': { borderRight: '1px dashed #ede9fe' } }}>
            {contractValue > 0 && <InlineStat label="มูลค่าสัญญา" value={contractValue} color="#7c3aed" tooltip="มูลค่าสัญญาโครงการ" />}
            <InlineStat label="งบรวม" value={totalBudget} color="#1d4ed8" tooltip="งบประมาณรวมทุกก้อน" />
            <InlineStat label="จ่ายแล้ว" value={totalSuccess} color="#15803d" tooltip="ค่าใช้จ่ายที่จ่ายแล้ว (SUCCESS)" />
            <InlineStat label="เจ้าหนี้" value={totalApprove} color="#b45309" tooltip="อนุมัติแล้ว ยังไม่จ่าย (APPROVE)" />
            <InlineStat label="คงเหลือ" value={remaining} color={isOver ? '#dc2626' : '#15803d'} tooltip={isOver ? 'เกินงบ!' : 'งบคงเหลือ'} />
            {laborCost > 0 && <InlineStat label="ค่าแรง" value={laborCost} color="text.secondary" tooltip="ค่าแรงงาน" />}
            {vat > 0 && <InlineStat label="VAT" value={vat} color="text.secondary" tooltip="VAT โครงการ" />}
            {isOver && (
              <Box sx={{ px: 1 }}>
                <Chip label="เกินงบ!" size="small" sx={{ fontSize: '0.6rem', fontWeight: 700, height: 18, backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5' }} />
              </Box>
            )}
          </Box>
        )}

        <Typography fontWeight={600} fontSize="0.85rem" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }}>
          {fmt(_.sumBy(expenses, 'total'))} ฿
        </Typography>
      </Box>

      <Collapse in={open}>
        <Divider />
        {budgetKeys.length === 0 ? (
          <Box sx={{ px: 3, py: 1.5 }}>
            <Typography variant="body2" color="text.secondary">ไม่มีรายการ</Typography>
          </Box>
        ) : (
          <BudgetTable budgetGroups={budgetGroups} budgetKeys={budgetKeys} payment={payment} />
        )}
      </Collapse>

      {/* ─── Project detail modal ─────────────────────────────────────────────── */}
      <Modal open={projModal} onClose={() => setProjModal(false)}>
        <Fade in={projModal}>
          <Box sx={{
            position: 'absolute',
top: '50%',
left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90vw',
maxWidth: 860,
maxHeight: '88vh',
            bgcolor: 'background.paper',
boxShadow: 24,
borderRadius: 2,
            display: 'flex',
flexDirection: 'column',
overflow: 'hidden',
          }}>
            {/* Modal header — Level 1 */}
            <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #ede9fe', flexShrink: 0, backgroundColor: '#faf5ff' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                    <Chip label="โครงการ" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, backgroundColor: '#ede9fe', color: '#7c3aed' }} />
                    <Typography fontWeight={700} fontSize="1rem" color="#7c3aed">{projectNumber}</Typography>
                    <Typography fontWeight={700} fontSize="1rem">{projectName}</Typography>
                  </Box>
                  {customer && (
                    <Typography variant="body2" color="text.secondary">
                      ลูกค้า: <b>{customer}</b>
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                    {contractValue > 0 && (
                      <Typography variant="caption" color="text.secondary">มูลค่าสัญญา: <b style={{ color: '#7c3aed' }}>฿{fmt(contractValue)}</b></Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">งบรวม: <b style={{ color: '#1d4ed8' }}>฿{fmt(totalBudget)}</b></Typography>
                    <Typography variant="caption" color="text.secondary">จ่ายแล้ว: <b style={{ color: '#15803d' }}>฿{fmt(totalSuccess)}</b></Typography>
                    <Typography variant="caption" color="text.secondary">เจ้าหนี้: <b style={{ color: '#b45309' }}>฿{fmt(totalApprove)}</b></Typography>
                    <Typography variant="caption" color={isOver ? 'error' : 'text.secondary'}>
                      คงเหลือ: <b>฿{fmt(remaining)}</b>{isOver && ' ⚠ เกินงบ'}
                    </Typography>
                  </Box>
                </Box>
                <IconButton size="small" onClick={() => setProjModal(false)}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>

            {/* Body — Level 2+3 budgets */}
            <Box sx={{ overflowY: 'auto', flex: 1, p: 1.5 }}>
              {projectBudgets.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">ไม่มีข้อมูลงบประมาณ</Typography>
                </Box>
              ) : (
                projectBudgets.map((budget) => (
                  <BudgetAccordion key={budget._id} budget={budget} />
                ))
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
}
